import { Verified } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useInsurance } from './hooks';
import { ViewInsuranceBenefitsModal } from './ViewInsuranceBenefitsModal';
import { EditInsurance } from '../EditInsurance';
import { components } from '@common/types/api.types';
import { isCashPaymentType } from '../EditInsurance/utils';

type InsuranceRecord = components['schemas']['PatientInsuranceRecordResponse'];

interface InsuranceProps {
  patient_id: string;
  disabled: boolean;
}

export const Insurance = ({ patient_id, disabled }: InsuranceProps) => {
  const {
    handleCloseBenefitsModal,
    handleOpenBenefitsModal,
    isBenefitsModalOpen,
    patientInsurances,
  } = useInsurance({ patient_id });

  const firstMedicare = patientInsurances?.primary_medicare[0];

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', mt: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography variant='subtitle2'>
            {t('patient.summary.insurance')}
          </Typography>
          <EditInsurance patient_id={patient_id} disabled={disabled} />
        </Box>

        <Card sx={{ borderRadius: 0 }}>
          <CardContent sx={{ p: '16px !important' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 1,
              }}
            >
              <Typography variant='subtitle2'>
                Primary insurance / Medicare
              </Typography>
              {firstMedicare && (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Typography
                    variant='body2'
                    sx={{
                      color: firstMedicare.verified
                        ? 'text.primary'
                        : 'text.disabled',
                    }}
                  >
                    {firstMedicare.verified
                      ? t('patient.summary.verified')
                      : t('patient.summary.notVerified')}
                  </Typography>
                  <Verified
                    color={firstMedicare.verified ? 'success' : 'disabled'}
                  />
                </Box>
              )}
            </Box>
            {(patientInsurances?.primary_medicare.length ?? 0) === 0 ? (
              <NotAddedText />
            ) : (
              <InsuranceRecordList
                records={patientInsurances!.primary_medicare}
                renderContent={record => (
                  <MedicareInsuranceContent
                    insurance={record}
                    handleOpenBenefitsModal={handleOpenBenefitsModal}
                  />
                )}
              />
            )}

            <Divider orientation='horizontal' sx={{ my: '1rem' }} />

            <InsuranceSectionHeader title='Primary insurance / Other' />
            {(patientInsurances?.primary_other.length ?? 0) === 0 ? (
              <NotAddedText />
            ) : (
              <InsuranceRecordList
                records={patientInsurances!.primary_other}
                renderContent={record => (
                  <OtherInsuranceContent insurance={record} />
                )}
              />
            )}

            <Divider orientation='horizontal' sx={{ my: '1rem' }} />

            <InsuranceSectionHeader title='Secondary insurance' />
            {(patientInsurances?.secondary.length ?? 0) === 0 ? (
              <NotAddedText />
            ) : (
              <InsuranceRecordList
                records={patientInsurances!.secondary}
                renderContent={record => (
                  <OtherInsuranceContent insurance={record} />
                )}
              />
            )}
          </CardContent>
        </Card>
      </Box>

      {firstMedicare && (
        <ViewInsuranceBenefitsModal
          onClose={handleCloseBenefitsModal}
          isOpen={isBenefitsModalOpen}
          payer_name={firstMedicare.payer_name || ''}
          payer_type={firstMedicare.payer_type || ''}
          payment_type={firstMedicare.payment_type || ''}
          benefits={firstMedicare.benefits || []}
        />
      )}
    </>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const InsuranceSectionHeader = ({ title }: { title: string }) => (
  <Typography variant='subtitle2' sx={{ pb: 1 }}>
    {title}
  </Typography>
);

const NotAddedText = () => (
  <Typography
    variant='body2'
    sx={{ color: theme => theme.palette.semiBlack.main }}
  >
    Not added.
  </Typography>
);

interface InsuranceRecordListProps {
  records: InsuranceRecord[];
  renderContent: (record: InsuranceRecord) => React.ReactNode;
}

const InsuranceRecordList = ({
  records,
  renderContent,
}: InsuranceRecordListProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {records.map((record, index) => (
      <Box key={record.id}>
        {index > 0 && <Divider sx={{ mb: 2 }} />}
        {renderContent(record)}
      </Box>
    ))}
  </Box>
);

// ─── Medicare content ─────────────────────────────────────────────────────────

interface MedicareInsuranceContentProps {
  insurance: InsuranceRecord;
  handleOpenBenefitsModal: () => void;
}

const MedicareInsuranceContent = ({
  insurance,
  handleOpenBenefitsModal,
}: MedicareInsuranceContentProps) => {
  const isFilled = !!(
    insurance.payer_name ||
    insurance.payer_type ||
    insurance.mbi ||
    insurance.payment_type
  );

  return (
    <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
      {isFilled ? (
        <>
          <InsuranceField
            label={t('patient.summary.payerName')}
            value={insurance.payer_name}
          />
          <InsuranceField
            label={t('patient.summary.payerType')}
            value={insurance.payer_type}
            capitalize
          />
          <InsuranceField
            label={t('patient.summary.paymentType')}
            value={insurance.payment_type}
            capitalize
          />
          <InsuranceField
            label={t('patient.summary.mbi')}
            value={insurance.mbi}
          />
          <InsuranceField
            label={t('patient.summary.from')}
            value={
              insurance.start_date
                ? dayjs(insurance.start_date, 'YYYY-MM-DD').format(
                    'MMM D, YYYY',
                  )
                : null
            }
          />
          <InsuranceField
            label={t('patient.summary.to')}
            value={
              insurance.end_date
                ? dayjs(insurance.end_date, 'YYYY-MM-DD').format('MMM D, YYYY')
                : null
            }
          />
          <InsuranceField
            label={t('patient.summary.group')}
            value={insurance.group_number}
          />
          <Button
            onClick={handleOpenBenefitsModal}
            disabled={!insurance.verified || insurance.benefits.length === 0}
            sx={{ mt: 1, alignSelf: 'flex-start' }}
          >
            {t('patient.summary.viewFullReport')}
          </Button>
        </>
      ) : (
        <NotAddedText />
      )}
    </Box>
  );
};

// ─── Other / Secondary content ────────────────────────────────────────────────

const OtherInsuranceContent = ({
  insurance,
}: {
  insurance: InsuranceRecord;
}) => {
  const isCash = isCashPaymentType(insurance.payment_type ?? '');

  const isFilled = !!(
    insurance.insurance_company ||
    insurance.insurance_id ||
    insurance.payment_type ||
    insurance.start_date
  );

  return (
    <Box>
      {isFilled ? (
        <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
          {!isCash && (
            <InsuranceField
              label='Insurance company'
              value={insurance.insurance_company_name}
              capitalize
            />
          )}
          {!isCash && (
            <InsuranceField
              label='Insurance ID'
              value={insurance.insurance_id}
              capitalize
            />
          )}
          <InsuranceField
            label={t('patient.summary.paymentType')}
            value={insurance.payment_type}
            capitalize
          />
          <InsuranceField
            label='Verbal consent date'
            value={
              insurance.consent?.verbal_consent_date
                ? dayjs(
                    insurance.consent.verbal_consent_date,
                    'YYYY-MM-DD',
                  ).format('MMM D, YYYY')
                : null
            }
          />
          <InsuranceField
            label='Consent given by'
            value={insurance.consent?.given_by}
          />
          <InsuranceField label='Witness' value={insurance.consent?.witness} />
        </Box>
      ) : (
        <NotAddedText />
      )}
    </Box>
  );
};

// ─── Shared field row ─────────────────────────────────────────────────────────

interface InsuranceFieldProps {
  label: string;
  value?: string | null;
  capitalize?: boolean;
}

const InsuranceField = ({ label, value, capitalize }: InsuranceFieldProps) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Typography
      variant='body2'
      sx={{ color: theme => theme.palette.semiBlack.main }}
    >
      {label}
    </Typography>
    <Typography
      variant='body2'
      sx={capitalize ? { textTransform: 'capitalize' } : undefined}
    >
      {value || '-'}
    </Typography>
  </Box>
);
